import { useCallback, useEffect, useMemo } from 'react';

import { Chat } from '@mint-sdk/api-ai-assistant';
import { differenceInSeconds } from 'date-fns';
import { atom, useAtom } from 'jotai';

import { useTextChat } from '../../../assistant/TextChatProvider.jsx';
import { useInterruptChatDialog } from './useInterruptChatDialog.jsx';

const interruptedAtom = atom(false);

const FAST_CANCEL_DURATION_IN_SECONDS = 30;

export const useInterruptChat = (chatId?: string) => {
  const [isInterrupted, setIsInterrupted] = useAtom(interruptedAtom);

  useEffect(() => {
    return () => {
      setIsInterrupted(false);
    };
  }, [chatId, setIsInterrupted]);

  const { chatMessages } = useTextChat();

  const {
    mutate: cancelRequest,
    isPending,
    isSuccess,
    reset: cancelRequestReset,
  } = Chat.cancelLastRequest();
  const { open: openInterruptDialog } = useInterruptChatDialog();

  const lastHumanMessage = useMemo(() => {
    return chatMessages
      .filter((message) => message.message.type === 'human')
      ?.at(-1);
  }, [chatMessages]);

  const interruptMutation = useCallback(
    (reason: string) => {
      if (lastHumanMessage && chatId) {
        cancelRequest({
          urlParams: {
            chatSessionId: chatId,
          },
          data: {
            reason,
          },
        });
      }
    },
    [cancelRequest, chatId, lastHumanMessage],
  );

  const isFastInterruption = (date: string) => {
    const requestDuration = differenceInSeconds(
      new Date(),
      new Date(date + 'Z'),
    );
    return requestDuration >= FAST_CANCEL_DURATION_IN_SECONDS;
  };

  const onInterrupt = useCallback(() => {
    if (!lastHumanMessage) return;

    setIsInterrupted(true);

    if (isFastInterruption(lastHumanMessage.createdAt)) {
      openInterruptDialog();
    } else {
      interruptMutation('');
    }
  }, [
    lastHumanMessage,
    interruptMutation,
    setIsInterrupted,
    openInterruptDialog,
  ]);

  return {
    isInterrupted,
    setIsInterrupted,
    interruptMutation,
    onInterrupt,
    onInterruptReset: cancelRequestReset,
    isInterruptSuccess: isSuccess,
    isInterruptPending: isPending,
  };
};
